import React, { useRef, useEffect } from 'react';
import { Application, Graphics } from 'pixi.js';
import { MonitorSnapshot, MonitorPosition, CycleSummaryEvent, CycleModelResult } from '../../../types/monitor.types';
import { TradeDialogData, IdleBehaviorState, AvatarEmotionalState } from '../types/tradingFloor.types';
import {
  CANVAS_WIDTH, CANVAS_HEIGHT, DESK_CONFIGS, TILE_SIZE, SCALE_FACTOR,
  MANAGER_PATROL, WATER_COOLER_X, WATER_COOLER_Y, AVATAR_WALK_SPEED,
  PRINTER_X, PRINTER_Y, COFFEE_X, COFFEE_Y,
  AISLE_ROWS, getNeighborDesk, computeWaypoints, CORRIDOR_X,
  DANCE_FLOOR_X, DANCE_FLOOR_Y, DANCE_FLOOR_W, DANCE_FLOOR_H,
  SLEEPING_SPOTS,
  OBSTACLES, OFFICE_BOUNDS, SLEEP_AREA_X, SLEEP_AREA_Y,
} from '../constants/officeLayout';
import { useAvatarStates } from '../hooks/useAvatarStates';
import { PALETTES, ColorPalette } from '../sprites/colorPalettes';
import {
  ANIMATION_FRAMES, WALKING_DOWN_FRAMES, IDLE_TYPING_UP_FRAMES,
  FEMALE_ANIMATION_FRAMES, FEMALE_WALKING_DOWN_FRAMES, FEMALE_IDLE_TYPING_UP_FRAMES,
  SLEEPING_FRAMES, DANCING_FRAMES,
} from '../sprites/avatarPixelData';
import { drawOfficeFloor } from './OfficeFloor';
import { drawDeskBack, drawDeskFront } from './DeskSprite';
import { isMarketOpen, getMarketStatus, MarketStatus } from '../../../utils/marketHours';

interface PixiOfficeProps {
  snapshot: MonitorSnapshot | null;
  cycleSummary: CycleSummaryEvent | null;
  onAvatarClick: (data: TradeDialogData) => void;
}

const PALETTE_MAP: Record<number, keyof ColorPalette> = {
  1: 'skin', 2: 'hair', 3: 'shirt', 4: 'pants', 5: 'shoes', 6: 'outline', 7: 'highlight',
};

// Avatar overlaps desk surface so it looks like they're sitting at the desk
// (avatar layer renders ON TOP of deskBack, so no visual collision)
const AVATAR_Y_OFFSET = -2;

// Models that use female sprites (long hair + skirt)
const FEMALE_MODELS = new Set(['GOI', 'LOI', 'QUINI', 'YENI']);

function drawAvatarOnGraphics(
  g: Graphics, frame: number[][], palette: ColorPalette,
  baseX: number, baseY: number, px: number
) {
  for (let row = 0; row < frame.length; row++) {
    for (let col = 0; col < frame[row].length; col++) {
      const val = frame[row][col];
      if (val === 0) continue;
      const colorKey = PALETTE_MAP[val];
      if (!colorKey) continue;
      g.rect(baseX + col * px, baseY + row * px, px, px);
      g.fill(palette[colorKey]);
    }
  }
}

// --- Pixel font for labels ---
const CHARS: Record<string, number[][]> = {
  'A': [[0,1,1,0],[1,0,0,1],[1,1,1,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,0,0,0]],
  'B': [[1,1,1,0],[1,0,0,1],[1,1,1,0],[1,0,0,1],[1,0,0,1],[1,1,1,0],[0,0,0,0]],
  'C': [[0,1,1,0],[1,0,0,1],[1,0,0,0],[1,0,0,0],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  'D': [[1,1,1,0],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,1,1,0],[0,0,0,0]],
  'E': [[1,1,1,1],[1,0,0,0],[1,1,1,0],[1,0,0,0],[1,0,0,0],[1,1,1,1],[0,0,0,0]],
  'F': [[1,1,1,1],[1,0,0,0],[1,1,1,0],[1,0,0,0],[1,0,0,0],[1,0,0,0],[0,0,0,0]],
  'G': [[0,1,1,0],[1,0,0,1],[1,0,0,0],[1,0,1,1],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  'H': [[1,0,0,1],[1,0,0,1],[1,1,1,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,0,0,0]],
  'I': [[0,1,1,0],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,1,1,0],[0,0,0,0]],
  'K': [[1,0,0,1],[1,0,1,0],[1,1,0,0],[1,0,1,0],[1,0,0,1],[1,0,0,1],[0,0,0,0]],
  'L': [[1,0,0,0],[1,0,0,0],[1,0,0,0],[1,0,0,0],[1,0,0,0],[1,1,1,1],[0,0,0,0]],
  'M': [[1,0,0,1],[1,1,1,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,0,0,0]],
  'N': [[1,0,0,1],[1,1,0,1],[1,0,1,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,0,0,0]],
  'O': [[0,1,1,0],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  'P': [[1,1,1,0],[1,0,0,1],[1,0,0,1],[1,1,1,0],[1,0,0,0],[1,0,0,0],[0,0,0,0]],
  'Q': [[0,1,1,0],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,1,0],[0,1,0,1],[0,0,0,0]],
  'R': [[1,1,1,0],[1,0,0,1],[1,0,0,1],[1,1,1,0],[1,0,1,0],[1,0,0,1],[0,0,0,0]],
  'S': [[0,1,1,1],[1,0,0,0],[0,1,1,0],[0,0,0,1],[0,0,0,1],[1,1,1,0],[0,0,0,0]],
  'T': [[1,1,1,1],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,0,0,0]],
  'U': [[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  'V': [[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,0,1,0],[0,0,0,0]],
  'W': [[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,1,1,1],[1,0,0,1],[0,0,0,0]],
  'X': [[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,1,1,0],[1,0,0,1],[1,0,0,1],[0,0,0,0]],
  'Y': [[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,0,0,0]],
  'Z': [[1,1,1,1],[0,0,0,1],[0,0,1,0],[0,1,0,0],[1,0,0,0],[1,1,1,1],[0,0,0,0]],
  '0': [[0,1,1,0],[1,0,0,1],[1,0,1,1],[1,1,0,1],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  '1': [[0,0,1,0],[0,1,1,0],[0,0,1,0],[0,0,1,0],[0,0,1,0],[0,1,1,1],[0,0,0,0]],
  '2': [[0,1,1,0],[1,0,0,1],[0,0,0,1],[0,0,1,0],[0,1,0,0],[1,1,1,1],[0,0,0,0]],
  '3': [[1,1,1,0],[0,0,0,1],[0,1,1,0],[0,0,0,1],[0,0,0,1],[1,1,1,0],[0,0,0,0]],
  '4': [[1,0,0,1],[1,0,0,1],[1,1,1,1],[0,0,0,1],[0,0,0,1],[0,0,0,1],[0,0,0,0]],
  '5': [[1,1,1,1],[1,0,0,0],[1,1,1,0],[0,0,0,1],[0,0,0,1],[1,1,1,0],[0,0,0,0]],
  '6': [[0,1,1,0],[1,0,0,0],[1,1,1,0],[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  '7': [[1,1,1,1],[0,0,0,1],[0,0,1,0],[0,0,1,0],[0,1,0,0],[0,1,0,0],[0,0,0,0]],
  '8': [[0,1,1,0],[1,0,0,1],[0,1,1,0],[1,0,0,1],[1,0,0,1],[0,1,1,0],[0,0,0,0]],
  '9': [[0,1,1,0],[1,0,0,1],[1,0,0,1],[0,1,1,1],[0,0,0,1],[0,1,1,0],[0,0,0,0]],
  '.': [[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,1,0,0],[0,0,0,0]],
  '+': [[0,0,0,0],[0,0,1,0],[0,1,1,1],[0,0,1,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]],
  '-': [[0,0,0,0],[0,0,0,0],[0,1,1,1],[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]],
  '$': [[0,0,1,0],[0,1,1,1],[1,0,0,0],[0,1,1,0],[0,0,0,1],[1,1,1,0],[0,0,1,0]],
  '/': [[0,0,0,1],[0,0,0,1],[0,0,1,0],[0,0,1,0],[0,1,0,0],[0,1,0,0],[0,0,0,0]],
  ' ': [[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]],
};

function drawPixelText(g: Graphics, text: string, x: number, y: number, color: number, scale: number = 1) {
  const charW = 5 * scale;
  for (let i = 0; i < text.length; i++) {
    const charPixels = CHARS[text[i].toUpperCase()] || CHARS[' '];
    for (let row = 0; row < charPixels.length; row++) {
      for (let col = 0; col < charPixels[row].length; col++) {
        if (charPixels[row][col]) {
          g.rect(x + i * charW + col * scale, y + row * scale, scale, scale);
          g.fill(color);
        }
      }
    }
  }
}

function drawNameLabel(g: Graphics, text: string, x: number, y: number, hasPosition: boolean) {
  const charW = 5;
  const padding = 4;
  const totalW = text.length * charW + padding * 2;
  g.roundRect(x - totalW / 2, y, totalW, 7 + padding * 2, 4);
  g.fill({ color: 0x1A1D2E, alpha: 0.85 });
  if (hasPosition) g.stroke({ width: 1, color: 0x7C6CF7 });
  drawPixelText(g, text, x - totalW / 2 + padding, y + padding, hasPosition ? 0xE2E8F0 : 0x94A3B8);
}

// --- Thought bubble system ---
interface ThoughtBubbleState {
  visible: boolean;
  symbolIndex: number;
  showTimer: number;
  hideTimer: number;
  fadeAlpha: number;
}

type SymbolId = 'anger' | 'sweat' | 'storm' | 'spiral'
  | 'money' | 'star' | 'uparrow' | 'music'
  | 'coffee' | 'zzz' | 'clipboard' | 'water' | 'paper';

function drawThoughtSymbol(g: Graphics, sym: SymbolId, cx: number, cy: number, s: number) {
  switch (sym) {
    case 'anger': // red cross lines
      g.moveTo(cx - 3 * s, cy - 3 * s); g.lineTo(cx + 3 * s, cy + 3 * s);
      g.stroke({ width: 2, color: 0xEF4444 });
      g.moveTo(cx + 3 * s, cy - 3 * s); g.lineTo(cx - 3 * s, cy + 3 * s);
      g.stroke({ width: 2, color: 0xEF4444 });
      break;
    case 'sweat': // blue teardrop
      g.moveTo(cx, cy - 4 * s);
      g.lineTo(cx + 2 * s, cy + 1 * s);
      g.arc(cx, cy + 1 * s, 2 * s, 0, Math.PI);
      g.closePath();
      g.fill(0x60A5FA);
      break;
    case 'storm': // cloud + bolt
      g.circle(cx - 2 * s, cy - 2 * s, 3 * s); g.fill(0x6B7280);
      g.circle(cx + 2 * s, cy - 2 * s, 2.5 * s); g.fill(0x6B7280);
      // lightning bolt
      g.moveTo(cx, cy); g.lineTo(cx + 1 * s, cy + 3 * s);
      g.lineTo(cx - 1 * s, cy + 2 * s); g.lineTo(cx, cy + 5 * s);
      g.stroke({ width: 1.5, color: 0xFDE68A });
      break;
    case 'spiral': // stress spiral
      for (let i = 0; i < 12; i++) {
        const a = i * 0.6;
        const r = (i * 0.4) * s;
        const px = cx + Math.cos(a) * r;
        const py = cy + Math.sin(a) * r;
        g.rect(px, py, s, s); g.fill(0xEF4444);
      }
      break;
    case 'money': // $ sign in circle
      g.circle(cx, cy, 4 * s); g.fill(0x166534);
      g.stroke({ width: 1, color: 0x22C55E });
      // $ drawn as lines
      g.rect(cx - 1 * s, cy - 3 * s, 2 * s, 1 * s); g.fill(0x22C55E);
      g.rect(cx - 2 * s, cy - 2 * s, 2 * s, 1 * s); g.fill(0x22C55E);
      g.rect(cx - 1 * s, cy - 1 * s, 3 * s, 1 * s); g.fill(0x22C55E);
      g.rect(cx + 1 * s, cy, 2 * s, 1 * s); g.fill(0x22C55E);
      g.rect(cx - 1 * s, cy + 1 * s, 2 * s, 1 * s); g.fill(0x22C55E);
      break;
    case 'star': // 5-point star
      g.star(cx, cy, 5, 4 * s, 2 * s); g.fill(0xFDE68A);
      break;
    case 'uparrow': // rising chart
      g.moveTo(cx - 4 * s, cy + 3 * s);
      g.lineTo(cx - 1 * s, cy); g.lineTo(cx + 1 * s, cy + 2 * s);
      g.lineTo(cx + 4 * s, cy - 3 * s);
      g.stroke({ width: 2, color: 0x22C55E });
      // arrowhead
      g.moveTo(cx + 2 * s, cy - 3 * s); g.lineTo(cx + 4 * s, cy - 3 * s);
      g.lineTo(cx + 4 * s, cy - 1 * s);
      g.stroke({ width: 1.5, color: 0x22C55E });
      break;
    case 'music': // music note
      g.circle(cx - 1 * s, cy + 2 * s, 2 * s); g.fill(0x7C6CF7);
      g.rect(cx + 1 * s, cy - 4 * s, 1 * s, 6 * s); g.fill(0x7C6CF7);
      g.rect(cx + 1 * s, cy - 4 * s, 3 * s, 1 * s); g.fill(0x7C6CF7);
      break;
    case 'coffee': // cup with steam
      g.roundRect(cx - 3 * s, cy - 1 * s, 6 * s, 5 * s, 1); g.fill(0xD4A574);
      g.stroke({ width: 1, color: 0x8B6914 });
      g.roundRect(cx - 2 * s, cy - 1 * s, 4 * s, 2 * s, 0); g.fill(0x5C3D2E);
      // steam
      g.moveTo(cx - 1 * s, cy - 2 * s); g.lineTo(cx, cy - 4 * s);
      g.stroke({ width: 1, color: 0x9CA3AF });
      g.moveTo(cx + 1 * s, cy - 2 * s); g.lineTo(cx + 2 * s, cy - 4 * s);
      g.stroke({ width: 1, color: 0x9CA3AF });
      break;
    case 'zzz': // sleeping z's
      drawPixelText(g, 'Z', cx - 3 * s, cy - 3 * s, 0x94A3B8, 1);
      drawPixelText(g, 'Z', cx, cy - 1 * s, 0x7C8AB8, 1);
      drawPixelText(g, 'Z', cx + 2 * s, cy + 2 * s, 0x64748B, 1);
      break;
    case 'clipboard': // paper with lines
      g.roundRect(cx - 3 * s, cy - 4 * s, 6 * s, 8 * s, 1); g.fill(0xE2E8F0);
      g.stroke({ width: 0.5, color: 0x94A3B8 });
      g.rect(cx - 2 * s, cy - 2 * s, 4 * s, 1 * s); g.fill(0x94A3B8);
      g.rect(cx - 2 * s, cy, 3 * s, 1 * s); g.fill(0x94A3B8);
      g.rect(cx - 2 * s, cy + 2 * s, 4 * s, 1 * s); g.fill(0x94A3B8);
      break;
    case 'water': // water drop
      g.moveTo(cx, cy - 4 * s);
      g.lineTo(cx + 3 * s, cy + 1 * s);
      g.arc(cx, cy + 1 * s, 3 * s, 0, Math.PI);
      g.closePath();
      g.fill(0x60A5FA);
      g.stroke({ width: 0.5, color: 0x3B82F6 });
      break;
    case 'paper': // printed page
      g.roundRect(cx - 3 * s, cy - 4 * s, 6 * s, 8 * s, 0); g.fill(0xF1F5F9);
      g.stroke({ width: 0.5, color: 0xE5E7EB });
      g.rect(cx - 2 * s, cy - 2 * s, 4 * s, 1 * s); g.fill(0x374151);
      g.rect(cx - 2 * s, cy, 3 * s, 1 * s); g.fill(0x374151);
      break;
  }
}

function drawThoughtBubble(
  g: Graphics, baseX: number, baseY: number, sym: SymbolId, alpha: number, px: number
) {
  const bubW = 24;
  const bubH = 20;
  const bubX = baseX + 14 * px;
  const bubY = baseY - 22;

  // Main bubble
  g.roundRect(bubX, bubY, bubW, bubH, 6);
  g.fill({ color: 0xFFFFFF, alpha: alpha * 0.92 });
  g.stroke({ width: 1, color: 0x94A3B8 });

  // Trailing thought circles
  g.circle(bubX + 3, bubY + bubH + 3, 3);
  g.fill({ color: 0xFFFFFF, alpha: alpha * 0.8 });
  g.circle(bubX, bubY + bubH + 8, 2);
  g.fill({ color: 0xFFFFFF, alpha: alpha * 0.65 });

  // Symbol inside
  drawThoughtSymbol(g, sym, bubX + bubW / 2, bubY + bubH / 2, 1.5);
}

function getThoughtSymbolPool(
  emotionalState: AvatarEmotionalState,
  hasTrade: boolean,
  behavior: IdleBehaviorState['behavior'],
  phase: IdleBehaviorState['phase']
): SymbolId[] {
  // Walking/at destination — show destination-relevant symbol
  if (phase === 'WALKING' || phase === 'AT_DESTINATION') {
    if (behavior === 'GETTING_COFFEE') return ['coffee'];
    if (behavior === 'GETTING_WATER') return ['water'];
    if (behavior === 'PRINTING') return ['paper'];
    if (behavior === 'CHATTING') return []; // chat bubble handles this
  }

  // Celebrating a win (trade closed in profit)
  if (emotionalState === 'celebrating_win') {
    return ['star', 'money', 'music', 'uparrow'];
  }

  // Stressed / losing
  if (emotionalState === 'stressed' || emotionalState === 'stressed_heavy') {
    return ['anger', 'sweat', 'storm', 'spiral'];
  }

  // Profitable / celebrating
  if (emotionalState === 'happy_working' || emotionalState === 'celebrating' || emotionalState === 'celebrating_big') {
    return ['money', 'star', 'uparrow', 'music'];
  }

  // Idle at desk (no trade)
  if (!hasTrade && phase === 'AT_DESK') {
    return ['coffee', 'zzz', 'clipboard', 'water'];
  }

  return []; // slumped, etc.
}

// --- Cycle analysis bubble ---
function drawCycleBubble(
  g: Graphics, baseX: number, baseY: number,
  result: CycleModelResult, alpha: number, px: number
) {
  const bubW = 30;
  const bubH = 22;
  const bubX = baseX + 14 * px;
  const bubY = baseY - 26;

  if (!result.checked) {
    // Grayed out — not evaluated
    g.roundRect(bubX, bubY, bubW, bubH, 4);
    g.fill({ color: 0x374151, alpha: alpha * 0.7 });
    g.stroke({ width: 1, color: 0x4B5563 });
    drawPixelText(g, 'NA', bubX + 9, bubY + 8, 0x6B7280);
  } else if (result.tradesEntered > 0) {
    // Green — trade entered
    g.roundRect(bubX, bubY, bubW, bubH, 4);
    g.fill({ color: 0x052E16, alpha: alpha * 0.9 });
    g.stroke({ width: 1.5, color: 0x22C55E });
    // Rising bars
    g.rect(bubX + 4, bubY + 14, 3, 4); g.fill({ color: 0x22C55E, alpha });
    g.rect(bubX + 8, bubY + 10, 3, 8); g.fill({ color: 0x22C55E, alpha });
    g.rect(bubX + 12, bubY + 6, 3, 12); g.fill({ color: 0x22C55E, alpha });
    // Checkmark
    g.moveTo(bubX + 20, bubY + 12);
    g.lineTo(bubX + 23, bubY + 15);
    g.lineTo(bubX + 28, bubY + 7);
    g.stroke({ width: 2, color: 0x22C55E });
    if (result.tradesEntered > 1) {
      drawPixelText(g, result.tradesEntered.toString(), bubX + 22, bubY + 2, 0x22C55E);
    }
  } else {
    // Red X — checked but no entry
    g.roundRect(bubX, bubY, bubW, bubH, 4);
    g.fill({ color: 0x1A1A2E, alpha: alpha * 0.9 });
    g.stroke({ width: 1, color: 0x6B7280 });
    // Flat/declining bars
    g.rect(bubX + 4, bubY + 10, 3, 8); g.fill({ color: 0x6B7280, alpha });
    g.rect(bubX + 8, bubY + 12, 3, 6); g.fill({ color: 0x6B7280, alpha });
    g.rect(bubX + 12, bubY + 14, 3, 4); g.fill({ color: 0x6B7280, alpha });
    // Red X
    const xCx = bubX + 23;
    const xCy = bubY + 11;
    g.moveTo(xCx - 3, xCy - 3); g.lineTo(xCx + 3, xCy + 3);
    g.stroke({ width: 2, color: 0xEF4444 });
    g.moveTo(xCx + 3, xCy - 3); g.lineTo(xCx - 3, xCy + 3);
    g.stroke({ width: 2, color: 0xEF4444 });
  }

  // Tail pointer
  g.moveTo(bubX + 4, bubY + bubH);
  g.lineTo(bubX + 1, bubY + bubH + 5);
  g.lineTo(bubX + 8, bubY + bubH);
  g.fill({ color: result.tradesEntered > 0 ? 0x052E16 : 0x1A1A2E, alpha: alpha * 0.9 });
}

// --- Persistent trade info bubble ---
function drawTradeBubble(
  g: Graphics, baseX: number, baseY: number,
  position: MonitorPosition, emotionalState: AvatarEmotionalState,
  tick: number, px: number, dcaCount: number
) {
  const isDCA = dcaCount > 1;
  const bubW = isDCA ? 52 : 44;
  const bubH = isDCA ? 32 : 24;
  const bubX = baseX + 14 * px;
  const bubY = baseY - (isDCA ? 36 : 28);

  const isProfit = position.currentPL >= 0;
  const bgColor = isProfit ? 0x052E16 : 0x2E0516;
  const borderColor = isProfit ? 0x22C55E : 0xEF4444;
  const plColor = isProfit ? 0x22C55E : 0xEF4444;

  // Card background
  g.roundRect(bubX, bubY, bubW, bubH, 4);
  g.fill({ color: bgColor, alpha: 0.92 });
  g.stroke({ width: 1.5, color: borderColor });

  // DCA badge (top-left)
  if (isDCA) {
    const badgeText = dcaCount + 'X';
    drawPixelText(g, badgeText, bubX + 3, bubY + 3, 0xFBBF24);
  }

  // Direction arrow (triangle)
  const arrowX = bubX + (isDCA ? 18 : 5);
  const arrowY = bubY + (isDCA ? 5 : 6);
  if (position.direction === 'BUY') {
    g.moveTo(arrowX, arrowY); g.lineTo(arrowX - 3, arrowY + 6); g.lineTo(arrowX + 3, arrowY + 6);
    g.closePath(); g.fill(0x22C55E);
  } else {
    g.moveTo(arrowX - 3, arrowY); g.lineTo(arrowX + 3, arrowY); g.lineTo(arrowX, arrowY + 6);
    g.closePath(); g.fill(0xEF4444);
  }

  // P/L text
  const plAbs = Math.abs(position.currentPL);
  const plText = (isProfit ? '+$' : '-$') + (plAbs >= 1000 ? (plAbs / 1000).toFixed(1) + 'K' : plAbs.toFixed(0));
  drawPixelText(g, plText, bubX + (isDCA ? 24 : 11), bubY + 4, plColor);

  // R value
  const rText = (position.currentR >= 0 ? '+' : '') + position.currentR.toFixed(1) + 'R';
  drawPixelText(g, rText, bubX + (isDCA ? 24 : 11), bubY + 14, 0x94A3B8);

  // DCA label row
  if (isDCA) {
    drawPixelText(g, 'DCA', bubX + 3, bubY + 23, 0x64748B);
  }

  // Pulsing glow for high R
  if (Math.abs(position.currentR) >= 1.5) {
    const pulse = 0.3 + 0.3 * Math.sin(tick * 0.15);
    g.roundRect(bubX - 1, bubY - 1, bubW + 2, bubH + 2, 5);
    g.stroke({ width: 1, color: borderColor, alpha: pulse });
  }

  // Tail pointer
  g.moveTo(bubX + 4, bubY + bubH);
  g.lineTo(bubX + 1, bubY + bubH + 5);
  g.lineTo(bubX + 8, bubY + bubH);
  g.fill({ color: bgColor, alpha: 0.92 });
}

// --- Confetti particle system ---
interface ConfettiParticle {
  x: number; y: number;
  vx: number; vy: number;
  color: number;
  life: number;
}

const CONFETTI_COLORS = [0xFDE68A, 0x22C55E, 0x7C6CF7, 0x60A5FA, 0xF472B6];

function spawnConfetti(cx: number, cy: number): ConfettiParticle[] {
  const particles: ConfettiParticle[] = [];
  for (let i = 0; i < 60; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1 + Math.random() * 3;
    particles.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      life: 120 + Math.floor(Math.random() * 120),
    });
  }
  return particles;
}

// --- Boss event + Office event interfaces ---
interface BossEvent {
  type: 'congratulate' | 'scold';
  targetX: number;
  targetY: number;
  modelName: string;
  timer: number;
  arrived: boolean;
}

// --- Boss off-hours roaming behavior ---
type BossOffHoursBehavior = 'VISITING_TRADER' | 'COUNTING_MONEY' | 'DANCING' | 'CHATTING' | 'COFFEE_BREAK' | 'WANDERING';

interface BossOffHoursState {
  behavior: BossOffHoursBehavior;
  phase: 'WALKING' | 'AT_DESTINATION';
  waypoints: { x: number; y: number }[];
  waypointIndex: number;
  timer: number;
  nextBehaviorIn: number;
  targetModel: string | null; // which trader they're visiting
}

// Build boss waypoints that route through the right corridor to avoid desks
function bossMakeWaypoints(fromX: number, fromY: number, toX: number, toY: number): { x: number; y: number }[] {
  // If staying roughly in the right corridor or bottom strip, go direct
  const inCorridor = fromX >= TILE_SIZE * 16 && toX >= TILE_SIZE * 16;
  const inBottom = fromY >= TILE_SIZE * 12 && toY >= TILE_SIZE * 12;
  if (inCorridor || inBottom || (Math.abs(fromY - toY) < TILE_SIZE * 2 && Math.abs(fromX - toX) < TILE_SIZE * 2)) {
    return [{ x: toX, y: toY }];
  }
  // Route through right corridor
  return [
    { x: CORRIDOR_X, y: fromY },
    { x: CORRIDOR_X, y: toY },
    { x: toX, y: toY },
  ];
}

function pickBossOffHoursBehavior(): BossOffHoursBehavior {
  const r = Math.random();
  if (r < 0.30) return 'VISITING_TRADER';    // 30% — visit a trader's desk to chat
  if (r < 0.50) return 'COUNTING_MONEY';     // 20% — count money at their own spot
  if (r < 0.65) return 'DANCING';            // 15% — join the dance floor
  if (r < 0.80) return 'CHATTING';           // 15% — chat near water cooler
  if (r < 0.92) return 'COFFEE_BREAK';       // 12% — get coffee
  return 'WANDERING';                         // 8% — wander the office
}

function createBossOffHoursTarget(behavior: BossOffHoursBehavior): { x: number; y: number; model: string | null } {
  switch (behavior) {
    case 'VISITING_TRADER': {
      const desk = DESK_CONFIGS[Math.floor(Math.random() * DESK_CONFIGS.length)];
      return { x: desk.gridX * TILE_SIZE + TILE_SIZE, y: desk.gridY * TILE_SIZE, model: desk.modelName };
    }
    case 'COUNTING_MONEY':
      // Sit in the sleeping area counting money
      return { x: SLEEP_AREA_X + TILE_SIZE * 2, y: SLEEP_AREA_Y + TILE_SIZE * 2 + 10, model: null };
    case 'DANCING':
      return {
        x: DANCE_FLOOR_X + Math.floor(Math.random() * DANCE_FLOOR_W),
        y: DANCE_FLOOR_Y + Math.floor(Math.random() * DANCE_FLOOR_H),
        model: null,
      };
    case 'CHATTING':
      // Chat in the lounge area
      return { x: TILE_SIZE * 10, y: TILE_SIZE * 16, model: null };
    case 'COFFEE_BREAK':
      return { x: COFFEE_X + 10, y: COFFEE_Y + 38, model: null };
    case 'WANDERING': {
      // Wander the lounge area
      return { x: TILE_SIZE * (2 + Math.random() * 16), y: TILE_SIZE * (14 + Math.random() * 5), model: null };
    }
  }
}

interface OfficeEvent {
  type: 'celebration' | 'mourning';
  modelName: string;
  displayName: string;
  timer: number;
}

// --- Monitor mini-game drawings (off-hours) ---
type GameType = 'tetris' | 'invaders' | 'snake' | 'pong';
const GAME_TYPES: GameType[] = ['tetris', 'invaders', 'snake', 'pong'];

function drawMonitorGame(g: Graphics, deskX: number, deskY: number, gameType: GameType, tick: number) {
  // Monitor screen area (from DeskSprite): x - 18, y - 44, 36x24
  const mx = deskX - 17;
  const my = deskY - 43;
  const mw = 34;
  const mh = 22;

  // Black background over the static chart
  g.rect(mx, my, mw, mh);
  g.fill(0x0A0A1A);

  switch (gameType) {
    case 'tetris': {
      const colors = [0xEF4444, 0x22C55E, 0x3B82F6, 0xFDE68A, 0xF472B6];
      // Stacked blocks at bottom
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 5; col++) {
          if ((row + col) % 3 !== 0) {
            g.rect(mx + 2 + col * 6, my + mh - 4 - row * 4, 5, 3);
            g.fill(colors[(row + col) % colors.length]);
          }
        }
      }
      // Falling piece
      const fallY = my + 2 + ((tick * 0.5) % (mh - 14));
      const fallX = mx + 8 + Math.floor((tick / 60) % 4) * 6;
      g.rect(fallX, fallY, 5, 3); g.fill(0x7C6CF7);
      g.rect(fallX, fallY + 3, 5, 3); g.fill(0x7C6CF7);
      break;
    }
    case 'invaders': {
      // 3 aliens moving side to side
      const alienOffset = Math.sin(tick * 0.03) * 8;
      for (let i = 0; i < 3; i++) {
        const ax = mx + 6 + i * 10 + alienOffset;
        const ay = my + 4;
        // Simple alien shape
        g.rect(ax, ay, 2, 2); g.fill(0x22C55E);
        g.rect(ax + 4, ay, 2, 2); g.fill(0x22C55E);
        g.rect(ax + 1, ay + 2, 4, 2); g.fill(0x22C55E);
        g.rect(ax, ay + 4, 6, 2); g.fill(0x22C55E);
      }
      // Player ship at bottom
      g.rect(mx + mw / 2 - 3, my + mh - 5, 6, 3); g.fill(0x60A5FA);
      g.rect(mx + mw / 2 - 1, my + mh - 7, 2, 2); g.fill(0x60A5FA);
      // Bullet
      const bulletY = my + mh - 8 - ((tick * 0.8) % (mh - 10));
      if (tick % 120 < 60) {
        g.rect(mx + mw / 2, bulletY, 1, 3); g.fill(0xFDE68A);
      }
      break;
    }
    case 'snake': {
      // Snake body segments moving in a path
      const segments = 6;
      for (let i = 0; i < segments; i++) {
        const phase = tick * 0.04 - i * 0.5;
        const sx = mx + mw / 2 + Math.cos(phase) * 10;
        const sy = my + mh / 2 + Math.sin(phase * 0.7) * 6;
        g.rect(sx, sy, 3, 3);
        g.fill(i === 0 ? 0x22C55E : 0x166534);
      }
      // Food pellet
      const foodX = mx + 6 + ((tick / 3) % 5) * 5;
      const foodY = my + 4 + ((tick / 7) % 3) * 6;
      g.rect(foodX, foodY, 3, 3); g.fill(0xEF4444);
      break;
    }
    case 'pong': {
      // Left paddle
      const lp = my + 4 + Math.sin(tick * 0.04) * 6;
      g.rect(mx + 2, lp, 2, 6); g.fill(0xE2E8F0);
      // Right paddle
      const rp = my + 4 + Math.sin(tick * 0.04 + 1) * 6;
      g.rect(mx + mw - 4, rp, 2, 6); g.fill(0xE2E8F0);
      // Ball
      const bx = mx + mw / 2 + Math.sin(tick * 0.06) * (mw / 2 - 6);
      const by = my + mh / 2 + Math.sin(tick * 0.08) * (mh / 2 - 4);
      g.rect(bx, by, 2, 2); g.fill(0xFDE68A);
      // Center line
      for (let i = 0; i < 5; i++) {
        g.rect(mx + mw / 2, my + 2 + i * 5, 1, 2); g.fill({ color: 0x4B5563, alpha: 0.5 });
      }
      break;
    }
  }
}

// --- Idle behavior helpers ---
const MAX_MOVERS = 3; // max avatars away from desk during market hours
const MAX_MOVERS_OFF_HOURS = 10; // off-hours: most traders should be off their desks having fun

function randomBehaviorDelay(): number {
  // 15-30 seconds at 60fps — mostly working
  return 900 + Math.floor(Math.random() * 900);
}

function pickRandomBehavior(): IdleBehaviorState['behavior'] {
  const r = Math.random();
  if (r < 0.74) return 'WORKING';         // 74% — mostly working
  if (r < 0.81) return 'GETTING_WATER';   // 7%
  if (r < 0.88) return 'CHATTING';        // 7%
  if (r < 0.94) return 'PRINTING';        // 6%
  return 'GETTING_COFFEE';                 // 6%
}

function pickOffHoursBehavior(): IdleBehaviorState['behavior'] {
  const r = Math.random();
  if (r < 0.30) return 'GAMING';           // 30% — playing games at desk monitor
  if (r < 0.35) return 'SLEEPING';         // 5% — sleeping bag on the floor
  if (r < 0.50) return 'DANCING';          // 15% — dancing at the dance floor
  if (r < 0.62) return 'CHATTING';         // 12% — chatting with neighbors
  if (r < 0.72) return 'GETTING_COFFEE';   // 10% — coffee break
  if (r < 0.82) return 'PARTYING';         // 10% — partying at water cooler
  if (r < 0.92) return 'GETTING_WATER';    // 10%
  return 'PRINTING';                        // 8%
}

function createDefaultBehavior(deskX: number, deskY: number): IdleBehaviorState {
  return {
    behavior: 'WORKING',
    phase: 'AT_DESK',
    currentX: deskX,
    currentY: deskY + AVATAR_Y_OFFSET,
    waypoints: [],
    waypointIndex: 0,
    timer: 0,
    nextBehaviorIn: randomBehaviorDelay(),
  };
}

function clampToBounds(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.max(OFFICE_BOUNDS.minX, Math.min(OFFICE_BOUNDS.maxX, x)),
    y: Math.max(OFFICE_BOUNDS.minY, Math.min(OFFICE_BOUNDS.maxY, y)),
  };
}

function collidesWithObstacle(x: number, y: number): boolean {
  // Check avatar center point against obstacle bounding boxes
  for (const obs of OBSTACLES) {
    if (x >= obs.x && x <= obs.x + obs.w && y >= obs.y && y <= obs.y + obs.h) {
      return true;
    }
  }
  return false;
}

function moveTowardPoint(state: IdleBehaviorState, tx: number, ty: number, speed: number): boolean {
  const dx = tx - state.currentX;
  const dy = ty - state.currentY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist <= speed) {
    state.currentX = tx;
    state.currentY = ty;
    const clamped = clampToBounds(state.currentX, state.currentY);
    state.currentX = clamped.x;
    state.currentY = clamped.y;
    return true;
  }
  const nextX = state.currentX + (dx / dist) * speed;
  const nextY = state.currentY + (dy / dist) * speed;
  // If next position collides with obstacle, try sliding along one axis
  if (collidesWithObstacle(nextX, nextY)) {
    if (!collidesWithObstacle(nextX, state.currentY)) {
      state.currentX = nextX;
    } else if (!collidesWithObstacle(state.currentX, nextY)) {
      state.currentY = nextY;
    }
    // else: stuck, don't move (will resolve next frame or skip waypoint)
  } else {
    state.currentX = nextX;
    state.currentY = nextY;
  }
  const clamped = clampToBounds(state.currentX, state.currentY);
  state.currentX = clamped.x;
  state.currentY = clamped.y;
  return false;
}

const PixiOffice: React.FC<PixiOfficeProps> = ({ snapshot, cycleSummary, onAvatarClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const avatarStates = useAvatarStates(snapshot);
  const avatarStatesRef = useRef(avatarStates);
  const snapshotRef = useRef(snapshot);
  const cycleSummaryRef = useRef(cycleSummary);
  const onAvatarClickRef = useRef(onAvatarClick);

  avatarStatesRef.current = avatarStates;
  snapshotRef.current = snapshot;
  cycleSummaryRef.current = cycleSummary;
  onAvatarClickRef.current = onAvatarClick;

  const frameCounterRef = useRef(0);
  const managerPosRef = useRef({ x: MANAGER_PATROL[0].x * TILE_SIZE, y: MANAGER_PATROL[0].y * TILE_SIZE });
  const managerWaypointRef = useRef(0);
  const managerPausedRef = useRef(false);
  const managerPauseTimerRef = useRef(0);
  const bossOffHoursRef = useRef<BossOffHoursState | null>(null);
  const behaviorMapRef = useRef<Record<string, IdleBehaviorState>>({});
  const sleepingSpotsUsedRef = useRef<Record<string, number>>({}); // modelName → spotIndex
  const thoughtBubbleMapRef = useRef<Record<string, ThoughtBubbleState>>({});

  // Cycle summary animation state
  const cycleAnimTimerRef = useRef(0);
  const prevCycleTimeRef = useRef<string | null>(null);
  const cycleBubbleDelayRef = useRef<Record<string, number>>({});

  // Win/loss event tracking
  const confettiRef = useRef<ConfettiParticle[]>([]);
  const bossEventRef = useRef<BossEvent | null>(null);
  const officeEventRef = useRef<OfficeEvent | null>(null);
  const prevTradeModelsRef = useRef<Set<string>>(new Set());

  // Market status (refreshed every 5 seconds in ticker)
  const marketStatusRef = useRef<MarketStatus>(getMarketStatus());
  const marketOpenRef = useRef(isMarketOpen());

  // Ticker scroll offset for banner
  const tickerOffsetRef = useRef(0);

  const floorGraphicsRef = useRef<Graphics | null>(null);
  const deskBackGraphicsRef = useRef<Graphics | null>(null);
  const avatarGraphicsRef = useRef<Graphics | null>(null);
  const deskFrontGraphicsRef = useRef<Graphics | null>(null);
  const managerGraphicsRef = useRef<Graphics | null>(null);
  const labelGraphicsRef = useRef<Graphics | null>(null);
  const wallAnimGraphicsRef = useRef<Graphics | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const app = new Application();
    appRef.current = app;

    const init = async () => {
      await app.init({
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundColor: 0x1a1d2e,
        antialias: false,
        resolution: 1,
      });

      if (!containerRef.current) return;
      containerRef.current.appendChild(app.canvas);

      // Create 7 layers in correct order
      const floorG = new Graphics();
      const wallAnimG = new Graphics();   // animated wall elements (ticker, whiteboard chart)
      const deskBackG = new Graphics();
      const avatarG = new Graphics();
      const deskFrontG = new Graphics();
      const managerG = new Graphics();
      const labelG = new Graphics();

      app.stage.addChild(floorG);
      app.stage.addChild(wallAnimG);
      app.stage.addChild(deskBackG);
      app.stage.addChild(avatarG);
      app.stage.addChild(deskFrontG);
      app.stage.addChild(managerG);
      app.stage.addChild(labelG);

      floorGraphicsRef.current = floorG;
      wallAnimGraphicsRef.current = wallAnimG;
      deskBackGraphicsRef.current = deskBackG;
      avatarGraphicsRef.current = avatarG;
      deskFrontGraphicsRef.current = deskFrontG;
      managerGraphicsRef.current = managerG;
      labelGraphicsRef.current = labelG;

      // Draw static layers
      drawOfficeFloor(floorG);
      for (const desk of DESK_CONFIGS) {
        drawDeskBack(deskBackG, desk.gridX * TILE_SIZE, desk.gridY * TILE_SIZE);
        drawDeskFront(deskFrontG, desk.gridX * TILE_SIZE, desk.gridY * TILE_SIZE);
      }

      // Initialize behaviors with staggered timers so they don't all move at once
      const bMap: Record<string, IdleBehaviorState> = {};
      for (let i = 0; i < DESK_CONFIGS.length; i++) {
        const desk = DESK_CONFIGS[i];
        const b = createDefaultBehavior(desk.gridX * TILE_SIZE, desk.gridY * TILE_SIZE);
        b.nextBehaviorIn = 300 + i * 180 + Math.floor(Math.random() * 300);
        bMap[desk.modelName] = b;
      }
      behaviorMapRef.current = bMap;

      // Initialize thought bubble states (staggered so they don't all pop at once)
      const tbMap: Record<string, ThoughtBubbleState> = {};
      for (let i = 0; i < DESK_CONFIGS.length; i++) {
        tbMap[DESK_CONFIGS[i].modelName] = {
          visible: false,
          symbolIndex: 0,
          showTimer: 0,
          hideTimer: 300 + i * 60 + Math.floor(Math.random() * 300),
          fadeAlpha: 0,
        };
      }
      thoughtBubbleMapRef.current = tbMap;

      // Click handlers
      avatarG.eventMode = 'static';
      avatarG.cursor = 'pointer';
      avatarG.on('pointerdown', (e) => {
        const pos = e.global;
        const states = avatarStatesRef.current;
        const bMapCur = behaviorMapRef.current;
        for (const avatar of states) {
          const bState = bMapCur[avatar.modelName];
          const drawX = bState ? bState.currentX - 8 * SCALE_FACTOR : avatar.deskX - 8 * SCALE_FACTOR;
          const drawY = bState ? bState.currentY : avatar.deskY + AVATAR_Y_OFFSET;
          if (pos.x >= drawX && pos.x <= drawX + 16 * SCALE_FACTOR &&
              pos.y >= drawY && pos.y <= drawY + 24 * SCALE_FACTOR) {
            onAvatarClickRef.current({
              modelName: avatar.modelName,
              displayName: avatar.displayName,
              position: avatar.position,
              allPositions: avatar.allPositions,
              screenX: pos.x,
              screenY: pos.y,
            });
            return;
          }
        }
      });

      managerG.eventMode = 'static';
      managerG.cursor = 'pointer';
      managerG.on('pointerdown', (e) => {
        const snap = snapshotRef.current;
        console.log('[TradingFloor] Manager clicked. Snapshot:', snap ? {
          totalPL: snap.totalPL,
          totalPositions: snap.totalPositions,
          positionsCount: snap.positions?.length,
          models: snap.positions?.map(p => p.model),
        } : 'null');
        onAvatarClickRef.current({
          modelName: 'MANAGER',
          displayName: 'The Boss',
          position: null,
          screenX: e.global.x,
          screenY: e.global.y,
          isManager: true,
          totalPL: snap?.totalPL ?? 0,
          totalPositions: snap?.totalPositions ?? 0,
        });
      });

      // ===== ANIMATION TICKER =====
      app.ticker.add(() => {
        frameCounterRef.current++;
        const tick = frameCounterRef.current;
        const frameIndex = Math.floor(tick / 30) % 2;

        // Refresh market status every 5 seconds (~300 frames)
        if (tick % 300 === 0) {
          marketStatusRef.current = getMarketStatus();
          marketOpenRef.current = isMarketOpen();
        }
        const offHours = !marketOpenRef.current;
        const mktStatus = marketStatusRef.current;

        // --- Animated wall elements (every 2 frames for perf) ---
        if (tick % 2 === 0) {
          const wg = wallAnimGraphicsRef.current;
          if (wg) {
            wg.clear();

            // === Scrolling trade ticker on banner ===
            const bannerX = TILE_SIZE * 3 + 6;
            const bannerY = TILE_SIZE * 0.2 + 6;
            const bannerW = TILE_SIZE * 12 - 12;
            const bannerH = TILE_SIZE * 1.3 - 12;

            // Build ticker text from snapshot (or cycle summary / events / off-hours)
            const snap = snapshotRef.current;
            const cycleData = cycleSummaryRef.current;
            const oEvt = officeEventRef.current;
            let tickerText = '  CODETREND TRADING FLOOR  ';
            if (offHours) {
              if (mktStatus === 'weekend') tickerText = '  MARKET CLOSED  WEEKEND  SEE YOU SUNDAY 4PM CST  MARKET CLOSED  ';
              else if (mktStatus === 'maintenance') tickerText = '  MAINTENANCE WINDOW  BACK AT 4PM CST  MAINTENANCE  ';
              else if (mktStatus === 'pre_open') tickerText = '  MARKET OPENS TODAY AT 4PM CST  PRE OPEN  ';
            } else if (oEvt && oEvt.type === 'celebration') {
              tickerText = `  ${oEvt.displayName} WINS  PROFIT TAKEN  GREAT TRADE  ${oEvt.displayName} WINS  `;
            } else if (oEvt && oEvt.type === 'mourning') {
              tickerText = `  ${oEvt.displayName} STOPPED OUT  LOSS TAKEN  ${oEvt.displayName} STOPPED OUT  `;
            } else if (cycleAnimTimerRef.current > 0 && cycleData) {
              tickerText = `  ${cycleData.summaryText}  `;
            } else if (snap && snap.positions.length > 0) {
              for (const p of snap.positions) {
                const pl = p.currentPL >= 0 ? `+$${p.currentPL.toFixed(0)}` : `-$${Math.abs(p.currentPL).toFixed(0)}`;
                tickerText += `  ${p.instrument} ${p.direction} ${pl}  `;
              }
            } else {
              tickerText += '  NO ACTIVE TRADES  STANDING BY  ';
            }

            // Scroll offset — scale=2 means each char is 10px wide
            const tickerScale = 2;
            const tickerCharW = 5 * tickerScale;
            tickerOffsetRef.current = (tickerOffsetRef.current + 0.8) % (tickerText.length * tickerCharW);
            const scrollX = -tickerOffsetRef.current;

            // Top row: title (scale=2) — changes during events / off-hours
            const titleText = offHours
              ? (mktStatus === 'weekend' ? 'MARKET CLOSED' : mktStatus === 'maintenance' ? 'MAINTENANCE' : 'PRE OPEN')
              : (oEvt && oEvt.type === 'celebration')
              ? `${oEvt.displayName} PROFIT`
              : (oEvt && oEvt.type === 'mourning')
              ? `${oEvt.displayName} STOPPED OUT`
              : (cycleAnimTimerRef.current > 0 && cycleData)
              ? `CYCLE SUMMARY ${cycleData.cycleTime} UTC`
              : 'CODETREND TRADING FLOOR';
            const titleX = bannerX + (bannerW - titleText.length * tickerCharW) / 2;
            drawPixelText(wg, titleText, titleX, bannerY + 4, 0x7C6CF7, tickerScale);

            // Bottom row: scrolling positions (scale=2, clipped to banner)
            const scrollY = bannerY + bannerH - 7 * tickerScale - 2;
            for (let i = 0; i < tickerText.length; i++) {
              const cx = bannerX + scrollX + i * tickerCharW;
              if (cx > bannerX - tickerCharW && cx < bannerX + bannerW) {
                const ch = tickerText[i].toUpperCase();
                const charPixels = CHARS[ch] || CHARS[' '];
                // Color: green for +, red for -, white otherwise
                let color = 0x94A3B8;
                if (ch === '+') color = 0x22C55E;
                else if (ch === '-') color = 0xEF4444;
                else if (tickerText.substring(Math.max(0, i - 3), i).includes('+')) color = 0x22C55E;
                else if (tickerText.substring(Math.max(0, i - 3), i).includes('-')) color = 0xEF4444;

                for (let row = 0; row < charPixels.length; row++) {
                  for (let col = 0; col < charPixels[row].length; col++) {
                    if (charPixels[row][col]) {
                      const cpx = cx + col * tickerScale;
                      if (cpx >= bannerX && cpx + tickerScale <= bannerX + bannerW) {
                        wg.rect(cpx, scrollY + row * tickerScale, tickerScale, tickerScale);
                        wg.fill(color);
                      }
                    }
                  }
                }
              }
            }

            // === Animated whiteboard chart ===
            const wbX = TILE_SIZE * 16 + 8;
            const wbY = TILE_SIZE * 0.15 + 8;
            const wbW = TILE_SIZE * 3.5 - 16;
            const wbH = TILE_SIZE * 1.6 - 16;

            // Draw animated chart line
            const chartColor = (snap?.totalPL ?? 0) >= 0 ? 0x22C55E : 0xEF4444;
            const points = 12;
            const phase = tick * 0.02;
            wg.moveTo(wbX, wbY + wbH * 0.5 + Math.sin(phase) * wbH * 0.3);
            for (let i = 1; i <= points; i++) {
              const px = wbX + (wbW / points) * i;
              const py = wbY + wbH * 0.5 + Math.sin(phase + i * 0.8) * wbH * 0.3;
              wg.lineTo(px, py);
            }
            wg.stroke({ width: 2, color: chartColor });

            // Draw a second line (moving average)
            wg.moveTo(wbX, wbY + wbH * 0.5 + Math.sin(phase * 0.7) * wbH * 0.15);
            for (let i = 1; i <= points; i++) {
              const px = wbX + (wbW / points) * i;
              const py = wbY + wbH * 0.5 + Math.sin(phase * 0.7 + i * 0.6) * wbH * 0.15;
              wg.lineTo(px, py);
            }
            wg.stroke({ width: 1, color: 0x60A5FA });

            // P/L label on whiteboard
            if (snap) {
              const plText = snap.totalPL >= 0 ? `+$${snap.totalPL.toFixed(0)}` : `-$${Math.abs(snap.totalPL).toFixed(0)}`;
              drawPixelText(wg, plText, wbX + 2, wbY + 2, chartColor);
            }

            // === Off-hours: animated dance floor glow ===
            if (offHours) {
              // Check if anyone is dancing
              const bMapCur = behaviorMapRef.current;
              const hasDancers = Object.values(bMapCur).some(b => b.behavior === 'DANCING' && b.phase === 'AT_DESTINATION');
              if (hasDancers) {
                // Pulsing colored tiles on the dance floor
                const tileS = 24;
                const cols = Math.floor(DANCE_FLOOR_W / tileS);
                const rows = Math.floor(DANCE_FLOOR_H / tileS);
                const discoColors = [0x7C6CF7, 0xF472B6, 0xFDE68A, 0x22C55E, 0x60A5FA, 0xEF4444];
                for (let r = 0; r < rows; r++) {
                  for (let c = 0; c < cols; c++) {
                    const ci = (r * cols + c + Math.floor(tick / 15)) % discoColors.length;
                    const alpha = 0.12 + Math.sin(tick * 0.06 + r + c * 1.5) * 0.08;
                    wg.rect(DANCE_FLOOR_X + c * tileS, DANCE_FLOOR_Y + r * tileS, tileS - 1, tileS - 1);
                    wg.fill({ color: discoColors[ci], alpha });
                  }
                }
                // Disco ball at center top
                const dbX = DANCE_FLOOR_X + DANCE_FLOOR_W / 2;
                const dbY = DANCE_FLOOR_Y - 10;
                wg.circle(dbX, dbY, 6);
                wg.fill(0xE2E8F0);
                wg.stroke({ width: 1, color: 0x94A3B8 });
                // Light rays from disco ball
                for (let ri = 0; ri < 5; ri++) {
                  const rayAngle = tick * 0.03 + ri * 1.26;
                  const rayLen = 30 + Math.sin(tick * 0.05 + ri) * 15;
                  wg.moveTo(dbX, dbY);
                  wg.lineTo(dbX + Math.cos(rayAngle) * rayLen, dbY + Math.abs(Math.sin(rayAngle)) * rayLen);
                  wg.stroke({ width: 1, color: discoColors[ri % discoColors.length], alpha: 0.3 });
                }
              }

            }
          }
        }

        // --- Update & draw avatars ---
        const ag = avatarGraphicsRef.current;
        if (ag) {
          ag.clear();
          const states = avatarStatesRef.current;
          const px = SCALE_FACTOR;
          const bMap = behaviorMapRef.current;

          // --- Detect new cycle summary → trigger animation ---
          const curCycle = cycleSummaryRef.current;
          if (curCycle && curCycle.isHourlyCycle && curCycle.cycleTime !== prevCycleTimeRef.current) {
            prevCycleTimeRef.current = curCycle.cycleTime;
            cycleAnimTimerRef.current = 900; // 15s at 60fps
            // Staggered bubble delays
            const delays: Record<string, number> = {};
            let delayIdx = 0;
            for (const desk of DESK_CONFIGS) {
              const mr = curCycle.models.find(m => m.modelName === desk.modelName);
              if (mr) { delays[desk.modelName] = delayIdx * 8; delayIdx++; }
            }
            cycleBubbleDelayRef.current = delays;
            // Recall all idle avatars to their desks
            for (const desk of DESK_CONFIGS) {
              const bs = bMap[desk.modelName];
              if (bs && bs.phase !== 'AT_DESK') {
                bs.behavior = 'WORKING';
                bs.phase = 'AT_DESK';
                bs.currentX = desk.gridX * TILE_SIZE;
                bs.currentY = desk.gridY * TILE_SIZE + AVATAR_Y_OFFSET;
                bs.nextBehaviorIn = 900;
              }
            }
          }
          if (cycleAnimTimerRef.current > 0) cycleAnimTimerRef.current--;

          // --- Detect trade close events (win/loss) ---
          const currentTradeModels = new Set(states.filter(a => a.position).map(a => a.modelName));
          Array.from(prevTradeModelsRef.current).forEach(prevModel => {
            if (!currentTradeModels.has(prevModel)) {
              const avatar = states.find(a => a.modelName === prevModel);
              const desk = DESK_CONFIGS.find(d => d.modelName === prevModel);
              if (avatar && desk) {
                if (avatar.emotionalState === 'celebrating_win') {
                  // Win! Party time!
                  officeEventRef.current = { type: 'celebration', modelName: prevModel, displayName: avatar.displayName, timer: 600 };
                  confettiRef.current = spawnConfetti(desk.gridX * TILE_SIZE, desk.gridY * TILE_SIZE);
                  bossEventRef.current = { type: 'congratulate', targetX: desk.gridX * TILE_SIZE + TILE_SIZE, targetY: desk.gridY * TILE_SIZE, modelName: prevModel, timer: 600, arrived: false };
                } else if (avatar.emotionalState === 'slumped') {
                  // Loss. Boss comes to scold.
                  officeEventRef.current = { type: 'mourning', modelName: prevModel, displayName: avatar.displayName, timer: 600 };
                  bossEventRef.current = { type: 'scold', targetX: desk.gridX * TILE_SIZE + TILE_SIZE, targetY: desk.gridY * TILE_SIZE, modelName: prevModel, timer: 600, arrived: false };
                }
              }
            }
          });
          prevTradeModelsRef.current = currentTradeModels;

          // Update office/boss event timers
          if (officeEventRef.current) {
            officeEventRef.current.timer--;
            if (officeEventRef.current.timer <= 0) officeEventRef.current = null;
          }
          if (bossEventRef.current) {
            bossEventRef.current.timer--;
            if (bossEventRef.current.timer <= 0) bossEventRef.current = null;
          }

          for (const avatar of states) {
            let bState = bMap[avatar.modelName];
            if (!bState) {
              bState = createDefaultBehavior(avatar.deskX, avatar.deskY);
              bMap[avatar.modelName] = bState;
            }

            const hasTrade = avatar.position !== null;
            const isSlumped = avatar.emotionalState === 'slumped';
            const deskHomeX = avatar.deskX;
            const deskHomeY = avatar.deskY + AVATAR_Y_OFFSET;
            const deskGridY = DESK_CONFIGS.find(d => d.modelName === avatar.modelName)?.gridY ?? 3;

            // --- Behavior state machine (suppressed during cycle animation, trade, slump, or win celebration) ---
            const isCelebratingWin = avatar.emotionalState === 'celebrating_win';
            if (!hasTrade && !isSlumped && !isCelebratingWin && cycleAnimTimerRef.current <= 0) {
              // Off-hours: if sitting at desk doing WORKING (not GAMING), force to GAMING immediately
              if (offHours && bState.phase === 'AT_DESK' && bState.behavior === 'WORKING') {
                bState.behavior = 'GAMING';
                bState.nextBehaviorIn = Math.min(bState.nextBehaviorIn, 600 + Math.floor(Math.random() * 600));
              }

              bState.nextBehaviorIn--;

              if (bState.phase === 'AT_DESK' && bState.nextBehaviorIn <= 0) {
                // Count how many are already moving — cap at MAX_MOVERS (higher off-hours)
                const moversCount = Object.values(bMap).filter(b => b.phase !== 'AT_DESK').length;
                const maxMovers = offHours ? MAX_MOVERS_OFF_HOURS : MAX_MOVERS;
                let newBehavior = offHours ? pickOffHoursBehavior() : pickRandomBehavior();
                if (moversCount >= maxMovers && newBehavior !== 'WORKING' && newBehavior !== 'GAMING') {
                  newBehavior = offHours ? 'GAMING' : 'WORKING';
                }
                bState.behavior = newBehavior;

                if (newBehavior === 'WORKING' || newBehavior === 'GAMING') {
                  // Stay at desk — off-hours: only GAMING allowed, shorter stay before next activity
                  if (offHours) newBehavior = 'GAMING';
                  bState.behavior = newBehavior;
                  bState.phase = 'AT_DESK';
                  bState.nextBehaviorIn = offHours ? 600 + Math.floor(Math.random() * 900) : randomBehaviorDelay();
                } else if (newBehavior === 'SLEEPING') {
                  // Find an unoccupied sleeping spot
                  const usedSpots = new Set(Object.values(sleepingSpotsUsedRef.current));
                  const freeSpots = SLEEPING_SPOTS.map((s, i) => i).filter(i => !usedSpots.has(i));
                  if (freeSpots.length > 0) {
                    const spotIdx = freeSpots[Math.floor(Math.random() * freeSpots.length)];
                    const spot = SLEEPING_SPOTS[spotIdx];
                    sleepingSpotsUsedRef.current[avatar.modelName] = spotIdx;
                    const wp = computeWaypoints(deskHomeX, deskHomeY, spot.x, spot.y, deskGridY);
                    bState.phase = 'WALKING';
                    bState.waypoints = wp;
                    bState.waypointIndex = 0;
                  } else {
                    // All spots taken, game at desk instead
                    bState.behavior = 'GAMING';
                    bState.phase = 'AT_DESK';
                    bState.nextBehaviorIn = 600 + Math.floor(Math.random() * 600);
                  }
                } else if (newBehavior === 'DANCING') {
                  // Walk to dance floor area with random offset
                  const danceX = DANCE_FLOOR_X + Math.floor(Math.random() * DANCE_FLOOR_W);
                  const danceY = DANCE_FLOOR_Y + Math.floor(Math.random() * DANCE_FLOOR_H);
                  const wp = computeWaypoints(deskHomeX, deskHomeY, danceX, danceY, deskGridY);
                  bState.phase = 'WALKING';
                  bState.waypoints = wp;
                  bState.waypointIndex = 0;
                } else if (newBehavior === 'PARTYING') {
                  // Walk to water cooler area for party
                  const wp = computeWaypoints(deskHomeX, deskHomeY, WATER_COOLER_X - 36, WATER_COOLER_Y + 10, deskGridY);
                  bState.phase = 'WALKING';
                  bState.waypoints = wp;
                  bState.waypointIndex = 0;
                } else if (newBehavior === 'GETTING_WATER') {
                  // Stand to the left of the water cooler
                  const wp = computeWaypoints(deskHomeX, deskHomeY, WATER_COOLER_X - 36, WATER_COOLER_Y, deskGridY);
                  bState.phase = 'WALKING';
                  bState.waypoints = wp;
                  bState.waypointIndex = 0;
                } else if (newBehavior === 'PRINTING') {
                  // Stand to the left of the printer
                  const wp = computeWaypoints(deskHomeX, deskHomeY, PRINTER_X - 30, PRINTER_Y + 20, deskGridY);
                  bState.phase = 'WALKING';
                  bState.waypoints = wp;
                  bState.waypointIndex = 0;
                } else if (newBehavior === 'GETTING_COFFEE') {
                  // Stand right at the coffee counter (table bottom edge)
                  const wp = computeWaypoints(deskHomeX, deskHomeY, COFFEE_X + 10, COFFEE_Y + 38, deskGridY);
                  bState.phase = 'WALKING';
                  bState.waypoints = wp;
                  bState.waypointIndex = 0;
                } else if (newBehavior === 'CHATTING') {
                  const neighbor = getNeighborDesk(avatar.modelName);
                  if (neighbor) {
                    // Stand next to the neighbor (offset to the side facing them)
                    const neighborDeskY = neighbor.gridY * TILE_SIZE + AVATAR_Y_OFFSET;
                    const chatX = neighbor.x > deskHomeX
                      ? neighbor.x - TILE_SIZE   // stand to the left of neighbor
                      : neighbor.x + TILE_SIZE;  // stand to the right
                    const wp = computeWaypoints(deskHomeX, deskHomeY, chatX, neighborDeskY, deskGridY);
                    bState.phase = 'WALKING';
                    bState.waypoints = wp;
                    bState.waypointIndex = 0;
                  } else {
                    bState.behavior = 'WORKING';
                    bState.phase = 'AT_DESK';
                    bState.nextBehaviorIn = randomBehaviorDelay();
                  }
                }
              }

              // Process walking through waypoints
              if (bState.phase === 'WALKING') {
                if (bState.waypointIndex < bState.waypoints.length) {
                  const wp = bState.waypoints[bState.waypointIndex];
                  const prevX = bState.currentX;
                  const prevY = bState.currentY;
                  const arrived = moveTowardPoint(bState, wp.x, wp.y, AVATAR_WALK_SPEED);
                  // Stuck detection: if barely moved, increment timer; skip waypoint after 45 frames stuck
                  const moved = Math.abs(bState.currentX - prevX) + Math.abs(bState.currentY - prevY);
                  if (!arrived && moved < 0.5) {
                    bState.timer++;
                    if (bState.timer > 45) {
                      // Skip this waypoint — teleport past the obstacle
                      bState.currentX = wp.x;
                      bState.currentY = wp.y;
                      bState.timer = 0;
                      bState.waypointIndex++;
                    }
                  } else {
                    bState.timer = 0;
                  }
                  if (arrived) {
                    bState.timer = 0;
                    bState.waypointIndex++;
                    if (bState.waypointIndex >= bState.waypoints.length) {
                      if (bState.behavior === 'WORKING') {
                        // Return trip complete — snap to desk
                        bState.phase = 'AT_DESK';
                        bState.currentX = deskHomeX;
                        bState.currentY = deskHomeY;
                        // Off-hours: very short desk stay, then leave again
                        bState.nextBehaviorIn = offHours ? 300 + Math.floor(Math.random() * 600) : randomBehaviorDelay();
                      } else {
                        // Arrived at destination
                        bState.phase = 'AT_DESTINATION';
                        bState.timer = bState.behavior === 'SLEEPING' ? 900 + Math.floor(Math.random() * 600)  // 15-25 sec nap
                          : bState.behavior === 'DANCING' ? 480 + Math.floor(Math.random() * 360)   // 8-14 sec dance


                          : bState.behavior === 'PARTYING' ? 300
                          : bState.behavior === 'GETTING_WATER' ? 150
                          : bState.behavior === 'GETTING_COFFEE' ? (offHours ? 300 : 180)
                          : bState.behavior === 'PRINTING' ? 180
                          : (offHours ? 240 : 160); // chatting
                      }
                    }
                  }
                }
              } else if (bState.phase === 'AT_DESTINATION') {
                bState.timer--;
                if (bState.timer <= 0) {
                  // Free sleeping spot if applicable
                  if (bState.behavior === 'SLEEPING') {
                    delete sleepingSpotsUsedRef.current[avatar.modelName];
                  }
                  // Walk back via waypoints (reverse path)
                  const wp = computeWaypoints(bState.currentX, bState.currentY, deskHomeX, deskHomeY, deskGridY);
                  bState.phase = 'WALKING';
                  bState.waypoints = wp;
                  bState.waypointIndex = 0;
                  // Mark as WORKING so when we arrive we go to AT_DESK
                  bState.behavior = 'WORKING';
                }
              }
            } else {
              // Has trade or slumped — snap to desk
              if (bState.phase !== 'AT_DESK') {
                // Free sleeping spot if snapping back
                if (bState.behavior === 'SLEEPING') {
                  delete sleepingSpotsUsedRef.current[avatar.modelName];
                }
                bState.behavior = 'WORKING';
                bState.phase = 'AT_DESK';
                bState.currentX = deskHomeX;
                bState.currentY = deskHomeY;
                bState.nextBehaviorIn = randomBehaviorDelay();
              }
            }

            // (return-to-desk is now handled inside the waypoint completion check above)

            // --- Determine animation frames (use female variants for GOI/LOI/QUINI/YENI) ---
            const isFemale = FEMALE_MODELS.has(avatar.modelName);
            const fMap = isFemale ? FEMALE_ANIMATION_FRAMES : ANIMATION_FRAMES;
            const fUpFrames = isFemale ? FEMALE_IDLE_TYPING_UP_FRAMES : IDLE_TYPING_UP_FRAMES;
            const fWalkFrames = isFemale ? FEMALE_WALKING_DOWN_FRAMES : WALKING_DOWN_FRAMES;

            let frames: number[][][];
            const isWalking = bState.phase === 'WALKING';
            const isAtDesk = bState.phase === 'AT_DESK';

            if (hasTrade) {
              // Always face monitor (UP) when in a trade
              frames = fMap[avatar.emotionalState + '_up'] || fUpFrames;
            } else if (isCelebratingWin) {
              // Celebrating a profitable close — use celebrating animation
              frames = fMap['celebrating'] || fMap['celebrating_big'] || fUpFrames;
            } else if (isWalking) {
              frames = fWalkFrames;
            } else if (bState.phase === 'AT_DESTINATION' && bState.behavior === 'SLEEPING') {
              frames = fMap['sleeping'] || SLEEPING_FRAMES;
            } else if (bState.phase === 'AT_DESTINATION' && bState.behavior === 'DANCING') {
              frames = fMap['dancing'] || DANCING_FRAMES;
            } else if (bState.phase === 'AT_DESTINATION') {
              frames = fMap['alert_standing'];
            } else if (isAtDesk) {
              frames = fUpFrames;
            } else {
              frames = fUpFrames;
            }

            const frame = frames[frameIndex % frames.length];
            const palette = PALETTES[avatar.paletteKey] || PALETTES['blue'];
            const baseX = bState.currentX - (8 * px);
            const baseY = bState.currentY;

            drawAvatarOnGraphics(ag, frame, palette, baseX, baseY, px);

            // Emotional indicators
            if (hasTrade) {
              if (avatar.emotionalState === 'celebrating_big' || avatar.emotionalState === 'celebrating') {
                const sparkleY = baseY - 8 - (tick % 16);
                ag.star(baseX + 4 * px, sparkleY, 4, 3, 1.5);
                ag.fill(avatar.emotionalState === 'celebrating_big' ? 0xFDE68A : 0x7C6CF7);
                ag.star(baseX + 12 * px, sparkleY - 5, 4, 3, 1.5);
                ag.fill(0xFDE68A);
              }
              if (avatar.emotionalState === 'stressed' || avatar.emotionalState === 'stressed_heavy') {
                const dropX = frameIndex % 2 === 0 ? baseX + 2 * px : baseX + 13 * px;
                const dropY = baseY + 2 * px + (tick % 12);
                ag.circle(dropX, dropY, 2);
                ag.fill(0x60A5FA);
              }
              if (avatar.emotionalState === 'alert_standing') {
                ag.rect(baseX + 7 * px, baseY - 16, 3, 8);
                ag.fill(0xFDE68A);
                ag.rect(baseX + 7 * px, baseY - 6, 3, 3);
                ag.fill(0xFDE68A);
              }
              if (avatar.position) {
                const dotColor = avatar.position.currentPL >= 0 ? 0x22C55E : 0xEF4444;
                ag.circle(baseX + 8 * px, baseY - 4, 4);
                ag.fill(dotColor);
                ag.stroke({ width: 1, color: 0x1A1A2E });
              }
            }

            // Dust puffs for walking
            if (isWalking && !hasTrade && tick % 20 < 10) {
              ag.circle(baseX + 8 * px, baseY + 24 * px + 2, 2);
              ag.fill({ color: 0x94A3B8, alpha: 0.4 });
            }

            // Chat bubble with animated dots when chatting at destination
            if (bState.behavior === 'CHATTING' && bState.phase === 'AT_DESTINATION') {
              const bubX = baseX + 16 * px;
              const bubY = baseY - 12;
              // Bubble background
              ag.roundRect(bubX, bubY, 24, 14, 4);
              ag.fill({ color: 0xFFFFFF, alpha: 0.9 });
              ag.stroke({ width: 1, color: 0x94A3B8 });
              // Bubble tail
              ag.moveTo(bubX + 2, bubY + 14);
              ag.lineTo(bubX - 3, bubY + 18);
              ag.lineTo(bubX + 6, bubY + 14);
              ag.fill({ color: 0xFFFFFF, alpha: 0.9 });
              // Animated dots (cycle through 1-3 visible)
              const dotCount = (Math.floor(tick / 20) % 3) + 1;
              for (let di = 0; di < dotCount; di++) {
                ag.circle(bubX + 6 + di * 6, bubY + 7, 2);
                ag.fill(0x374151);
              }
            }

            // Off-hours: draw mini-game on monitor when GAMING
            if (offHours && bState.behavior === 'GAMING' && bState.phase === 'AT_DESK') {
              const deskIdx = DESK_CONFIGS.findIndex(d => d.modelName === avatar.modelName);
              const gameType = GAME_TYPES[deskIdx % GAME_TYPES.length];
              drawMonitorGame(ag, avatar.deskX, avatar.deskY, gameType, tick);
            }

            // Off-hours: sparkles for partying avatars
            if (offHours && bState.behavior === 'PARTYING' && bState.phase === 'AT_DESTINATION') {
              const sparkleY = baseY - 8 - (tick % 16);
              ag.star(baseX + 4 * px, sparkleY, 4, 3, 1.5);
              ag.fill(0xF472B6);
              ag.star(baseX + 12 * px, sparkleY - 5, 4, 3, 1.5);
              ag.fill(0xFDE68A);
              ag.star(baseX + 2 * px, sparkleY + 3, 4, 2, 1);
              ag.fill(0x7C6CF7);
            }

            // Off-hours: sleeping bag outline + Zzz floating above
            if (offHours && bState.behavior === 'SLEEPING' && bState.phase === 'AT_DESTINATION') {
              // Sleeping bag drawn around the avatar (cozy rectangle under them)
              const bagX = baseX - 2;
              const bagY = baseY + 14 * px;
              const bagW = 16 * px + 4;
              const bagH = 10 * px + 4;
              ag.roundRect(bagX, bagY, bagW, bagH, 4);
              ag.fill({ color: palette.shirt, alpha: 0.5 });
              ag.stroke({ width: 1.5, color: palette.outline });
              // Pillow
              ag.roundRect(baseX + 9 * px, bagY - 2, 6 * px, 4 * px, 3);
              ag.fill(0xE2E8F0);
              ag.stroke({ width: 0.5, color: 0x94A3B8 });
              // Floating Zzz
              const zOff = (tick * 0.5) % 30;
              const zAlpha = 1 - zOff / 30;
              ag.setStrokeStyle({ width: 0 });
              drawPixelText(ag, 'Z', baseX + 13 * px, baseY + 6 * px - zOff, (0x94A3B8 & 0xFFFFFF));
              drawPixelText(ag, 'Z', baseX + 15 * px, baseY + 2 * px - zOff - 8, (0x7C8AB8 & 0xFFFFFF));
            }

            // Off-hours: dance floor effects — bouncing + music notes + disco sparkles
            if (offHours && bState.behavior === 'DANCING' && bState.phase === 'AT_DESTINATION') {
              // Colored sparkles around dancer
              const colors = [0xF472B6, 0xFDE68A, 0x7C6CF7, 0x22C55E, 0x60A5FA];
              for (let si = 0; si < 3; si++) {
                const angle = (tick * 0.05 + si * 2.1);
                const sparkR = 20 + Math.sin(tick * 0.08 + si) * 8;
                const sx = baseX + 8 * px + Math.cos(angle) * sparkR;
                const sy = baseY + 10 * px + Math.sin(angle) * sparkR;
                ag.star(sx, sy, 4, 3, 1.5);
                ag.fill(colors[(si + Math.floor(tick / 30)) % colors.length]);
              }
              // Floating music notes
              const noteY = baseY - 10 - (tick % 24);
              ag.circle(baseX + 3 * px, noteY, 2);
              ag.fill(0x7C6CF7);
              ag.rect(baseX + 3 * px + 2, noteY - 6, 1, 6);
              ag.fill(0x7C6CF7);
              ag.circle(baseX + 13 * px, noteY - 8, 2);
              ag.fill(0xF472B6);
              ag.rect(baseX + 13 * px + 2, noteY - 14, 1, 6);
              ag.fill(0xF472B6);
            }

            // Win celebration sparkles for celebrating_win avatars
            if (isCelebratingWin) {
              const sparkleY = baseY - 8 - (tick % 16);
              ag.star(baseX + 4 * px, sparkleY, 4, 3, 1.5);
              ag.fill(0xFDE68A);
              ag.star(baseX + 12 * px, sparkleY - 5, 4, 3, 1.5);
              ag.fill(0x7C6CF7);
              ag.star(baseX + 2 * px, sparkleY + 3, 4, 2, 1);
              ag.fill(0x22C55E);
            }

            // --- Cycle analysis bubbles (override thought bubbles during cycle) ---
            if (cycleAnimTimerRef.current > 0 && curCycle) {
              const modelResult = curCycle.models.find(m => m.modelName === avatar.modelName);
              if (modelResult) {
                const staggerDelay = cycleBubbleDelayRef.current[avatar.modelName] ?? 0;
                const elapsed = 900 - cycleAnimTimerRef.current;
                if (elapsed >= staggerDelay) {
                  const bubbleAge = elapsed - staggerDelay;
                  let alpha = 1.0;
                  if (bubbleAge < 15) alpha = bubbleAge / 15;
                  if (cycleAnimTimerRef.current < 60) alpha = cycleAnimTimerRef.current / 60;
                  drawCycleBubble(ag, baseX, baseY, modelResult, alpha, px);
                }
              }
            } else if (hasTrade && avatar.position) {
              // --- Persistent trade info bubble (always visible) ---
              drawTradeBubble(ag, baseX, baseY, avatar.position, avatar.emotionalState, tick, px, avatar.allPositions.length);
            } else {
              // --- Thought bubbles (normal mode) ---
              const isChatting = bState.behavior === 'CHATTING' && bState.phase === 'AT_DESTINATION';
              if (!isChatting) {
                const tbState = thoughtBubbleMapRef.current[avatar.modelName];
                if (tbState) {
                  // During office celebration, force celebration symbols on all idle avatars
                  // During off-hours, show fun symbols
                  const oEvt = officeEventRef.current;
                  let symbolPool: SymbolId[];
                  if (offHours && !hasTrade) {
                    if (bState.behavior === 'GAMING') symbolPool = ['star', 'music'];
                    else if (bState.behavior === 'PARTYING') symbolPool = ['music', 'star'];
                    else if (bState.behavior === 'SLEEPING') symbolPool = ['zzz'];
                    else if (bState.behavior === 'DANCING') symbolPool = ['music', 'star'];
                    else symbolPool = ['music', 'coffee', 'zzz', 'star'];
                  } else if (oEvt && oEvt.type === 'celebration' && oEvt.modelName !== avatar.modelName) {
                    symbolPool = ['star', 'music'];
                  } else {
                    symbolPool = getThoughtSymbolPool(
                      avatar.emotionalState, hasTrade, bState.behavior, bState.phase
                    );
                  }

                  if (symbolPool.length > 0) {
                    if (tbState.visible) {
                      tbState.showTimer--;
                      if (tbState.fadeAlpha < 1) {
                        tbState.fadeAlpha = Math.min(1, tbState.fadeAlpha + 0.08);
                      }
                      if (tbState.showTimer % 120 === 0 && tbState.showTimer > 0) {
                        tbState.symbolIndex = (tbState.symbolIndex + 1) % symbolPool.length;
                      }
                      if (tbState.showTimer <= 12) {
                        tbState.fadeAlpha = Math.max(0, tbState.fadeAlpha - 0.08);
                      }
                      if (tbState.showTimer <= 0) {
                        tbState.visible = false;
                        tbState.fadeAlpha = 0;
                        tbState.hideTimer = 360 + Math.floor(Math.random() * 420);
                      }
                    } else {
                      tbState.hideTimer--;
                      if (tbState.hideTimer <= 0) {
                        tbState.visible = true;
                        tbState.fadeAlpha = 0;
                        tbState.showTimer = 180 + Math.floor(Math.random() * 180);
                        tbState.symbolIndex = Math.floor(Math.random() * symbolPool.length);
                      }
                    }

                    if (tbState.visible && tbState.fadeAlpha > 0) {
                      const sym = symbolPool[tbState.symbolIndex % symbolPool.length];
                      drawThoughtBubble(ag, baseX, baseY, sym, tbState.fadeAlpha, px);
                    }
                  } else {
                    tbState.visible = false;
                    tbState.fadeAlpha = 0;
                  }
                }
              }
            }
          }
        }

        // --- Confetti particles ---
        if (confettiRef.current.length > 0) {
          const cg = avatarGraphicsRef.current;
          if (cg) {
            const alive: ConfettiParticle[] = [];
            for (const p of confettiRef.current) {
              p.x += p.vx;
              p.y += p.vy;
              p.vy += 0.05; // gravity
              p.life--;
              if (p.life > 0) {
                const alpha = Math.min(1, p.life / 30);
                cg.rect(p.x, p.y, 3, 3);
                cg.fill({ color: p.color, alpha });
                alive.push(p);
              }
            }
            confettiRef.current = alive;
          }
        }

        // --- Manager ---
        const mg = managerGraphicsRef.current;
        if (mg) {
          mg.clear();
          const mPos = managerPosRef.current;
          const mPx = SCALE_FACTOR;

          const bossEvt = bossEventRef.current;
          let bossIsMoving = false;

          if (offHours && !bossEvt) {
            // Off-hours: boss roams the office having fun
            let boh = bossOffHoursRef.current;
            if (!boh) {
              // Initialize first off-hours behavior
              const behavior = pickBossOffHoursBehavior();
              const target = createBossOffHoursTarget(behavior);
              const wp = bossMakeWaypoints(mPos.x, mPos.y, target.x, target.y);
              boh = {
                behavior,
                phase: 'WALKING',
                waypoints: wp,
                waypointIndex: 0,
                timer: 0,
                nextBehaviorIn: 0,
                targetModel: target.model,
              };
              bossOffHoursRef.current = boh;
            }

            if (boh.phase === 'WALKING') {
              if (boh.waypointIndex < boh.waypoints.length) {
                const wp = boh.waypoints[boh.waypointIndex];
                const dx = wp.x - mPos.x;
                const dy = wp.y - mPos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > 1.5) {
                  mPos.x += (dx / dist) * 1.0;
                  mPos.y += (dy / dist) * 1.0;
                  bossIsMoving = true;
                } else {
                  mPos.x = wp.x;
                  mPos.y = wp.y;
                  boh.waypointIndex++;
                }
              }
              if (boh.waypointIndex >= boh.waypoints.length) {
                boh.phase = 'AT_DESTINATION';
                // Set timer based on behavior
                boh.timer = boh.behavior === 'COUNTING_MONEY' ? 480 + Math.floor(Math.random() * 300)
                  : boh.behavior === 'DANCING' ? 360 + Math.floor(Math.random() * 240)
                  : boh.behavior === 'VISITING_TRADER' ? 240 + Math.floor(Math.random() * 180)
                  : boh.behavior === 'CHATTING' ? 300 + Math.floor(Math.random() * 180)
                  : boh.behavior === 'COFFEE_BREAK' ? 240 + Math.floor(Math.random() * 120)
                  : 180 + Math.floor(Math.random() * 120); // wandering
              }
            } else {
              // AT_DESTINATION — count down then pick new behavior
              boh.timer--;
              if (boh.timer <= 0) {
                const behavior = pickBossOffHoursBehavior();
                const target = createBossOffHoursTarget(behavior);
                const wp = bossMakeWaypoints(mPos.x, mPos.y, target.x, target.y);
                boh.behavior = behavior;
                boh.phase = 'WALKING';
                boh.waypoints = wp;
                boh.waypointIndex = 0;
                boh.targetModel = target.model;
              }
            }
          } else if (!offHours) {
            // Market just opened — clear off-hours state
            bossOffHoursRef.current = null;
          }

          if (bossEvt) {
            // Boss event override — walk to winner/loser desk
            if (!bossEvt.arrived) {
              const speed = 1.5; // faster than normal
              const dx = bossEvt.targetX - mPos.x;
              const dy = bossEvt.targetY - mPos.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist <= speed) {
                mPos.x = bossEvt.targetX;
                mPos.y = bossEvt.targetY;
                bossEvt.arrived = true;
              } else {
                mPos.x += (dx / dist) * speed;
                mPos.y += (dy / dist) * speed;
                bossIsMoving = true;
              }
            }
            // When arrived, boss pauses at desk
          } else {
            // Normal patrol
            if (!managerPausedRef.current) {
              const target = MANAGER_PATROL[managerWaypointRef.current];
              const targetX = target.x * TILE_SIZE;
              const targetY = target.y * TILE_SIZE;
              const speed = 0.8;
              const dx = targetX - mPos.x;
              const dy = targetY - mPos.y;
              if (Math.abs(dx) > speed) { mPos.x += Math.sign(dx) * speed; bossIsMoving = true; }
              else mPos.x = targetX;
              if (Math.abs(dy) > speed) { mPos.y += Math.sign(dy) * speed; bossIsMoving = true; }
              else mPos.y = targetY;
              if (Math.abs(mPos.x - targetX) < 1 && Math.abs(mPos.y - targetY) < 1) {
                managerPausedRef.current = true;
                managerPauseTimerRef.current = 180;
              }
            } else {
              managerPauseTimerRef.current--;
              if (managerPauseTimerRef.current <= 0) {
                managerPausedRef.current = false;
                managerWaypointRef.current = (managerWaypointRef.current + 1) % MANAGER_PATROL.length;
              }
            }
          }

          const snap = snapshotRef.current;
          const isHappy = (snap?.totalPL ?? 0) >= 0;
          const boh = bossOffHoursRef.current;
          const bossDancing = offHours && boh && boh.behavior === 'DANCING' && boh.phase === 'AT_DESTINATION';
          const mFrames = bossIsMoving ? WALKING_DOWN_FRAMES : bossDancing ? DANCING_FRAMES : IDLE_TYPING_UP_FRAMES;
          const mFrame = mFrames[Math.floor(tick / (bossDancing ? 15 : 30)) % mFrames.length];
          const mPalette = PALETTES['manager'];
          const baseX = mPos.x - (8 * mPx);
          const baseY = mPos.y - (4 * mPx);

          drawAvatarOnGraphics(mg, mFrame, mPalette, baseX, baseY, mPx);

          // Manager tie (relaxed blue during off-hours)
          mg.rect(baseX + 7 * mPx, baseY + 9 * mPx, 2 * mPx, 4 * mPx);
          mg.fill(offHours ? 0x60A5FA : (isHappy ? 0x22C55E : 0xEF4444));

          // Mood indicator (small emoji-like dot)
          mg.circle(baseX + 8 * mPx, baseY - 6, 3);
          mg.fill(offHours ? 0x60A5FA : (isHappy ? 0x22C55E : 0xEF4444));

          // Off-hours: boss thought bubble + effects based on behavior
          if (offHours && !bossIsMoving) {
            const boh = bossOffHoursRef.current;
            if (boh && boh.phase === 'AT_DESTINATION') {
              if (boh.behavior === 'COUNTING_MONEY') {
                // Money symbol + animated dollar signs floating up
                drawThoughtBubble(mg, baseX, baseY, 'money', 0.9, mPx);
                // Floating $ signs
                const dollarY = baseY - 14 - (tick % 20);
                drawPixelText(mg, '$', baseX + 5 * mPx, dollarY, 0x22C55E);
                drawPixelText(mg, '$', baseX + 11 * mPx, dollarY - 8, 0xFDE68A);
              } else if (boh.behavior === 'DANCING') {
                // Music notes + sparkles
                const colors = [0xF472B6, 0xFDE68A, 0x7C6CF7];
                for (let si = 0; si < 2; si++) {
                  const angle = tick * 0.05 + si * 3.14;
                  const sx = baseX + 8 * mPx + Math.cos(angle) * 18;
                  const sy = baseY + 8 * mPx + Math.sin(angle) * 14;
                  mg.star(sx, sy, 4, 3, 1.5);
                  mg.fill(colors[(si + Math.floor(tick / 30)) % colors.length]);
                }
                drawThoughtBubble(mg, baseX, baseY, 'music', 0.85, mPx);
              } else if (boh.behavior === 'VISITING_TRADER') {
                // Chat bubble with animated dots (same as trader chatting)
                const bubX = baseX + 16 * mPx;
                const bubY = baseY - 12;
                mg.roundRect(bubX, bubY, 24, 14, 4);
                mg.fill({ color: 0xFFFFFF, alpha: 0.9 });
                mg.stroke({ width: 1, color: 0x94A3B8 });
                mg.moveTo(bubX + 2, bubY + 14);
                mg.lineTo(bubX - 3, bubY + 18);
                mg.lineTo(bubX + 6, bubY + 14);
                mg.fill({ color: 0xFFFFFF, alpha: 0.9 });
                const dotCount = (Math.floor(tick / 20) % 3) + 1;
                for (let di = 0; di < dotCount; di++) {
                  mg.circle(bubX + 6 + di * 6, bubY + 7, 2);
                  mg.fill(0x374151);
                }
              } else if (boh.behavior === 'CHATTING') {
                drawThoughtBubble(mg, baseX, baseY, 'star', 0.85, mPx);
              } else if (boh.behavior === 'COFFEE_BREAK') {
                drawThoughtBubble(mg, baseX, baseY, 'coffee', 0.85, mPx);
              } else {
                // Wandering — alternating thoughts
                const bSym: SymbolId = (Math.floor(tick / 180) % 3 === 0) ? 'star' : (Math.floor(tick / 180) % 3 === 1) ? 'music' : 'coffee';
                drawThoughtBubble(mg, baseX, baseY, bSym, 0.7, mPx);
              }
            } else {
              // Walking — no bubble
            }
          }

          // Boss speech bubble at event target
          if (bossEvt && bossEvt.arrived) {
            const sBubX = baseX + 16 * mPx;
            const sBubY = baseY - 18;
            mg.roundRect(sBubX, sBubY, 28, 16, 4);
            if (bossEvt.type === 'congratulate') {
              mg.fill({ color: 0x052E16, alpha: 0.92 });
              mg.stroke({ width: 1.5, color: 0x22C55E });
              mg.star(sBubX + 14, sBubY + 8, 5, 5, 2.5);
              mg.fill(0xFDE68A);
            } else {
              mg.fill({ color: 0x2E0516, alpha: 0.92 });
              mg.stroke({ width: 1.5, color: 0xEF4444 });
              // Angry X
              mg.moveTo(sBubX + 9, sBubY + 4); mg.lineTo(sBubX + 19, sBubY + 12);
              mg.stroke({ width: 2, color: 0xEF4444 });
              mg.moveTo(sBubX + 19, sBubY + 4); mg.lineTo(sBubX + 9, sBubY + 12);
              mg.stroke({ width: 2, color: 0xEF4444 });
            }
            // Tail
            mg.moveTo(sBubX + 2, sBubY + 16);
            mg.lineTo(sBubX - 2, sBubY + 20);
            mg.lineTo(sBubX + 6, sBubY + 16);
            mg.fill({ color: bossEvt.type === 'congratulate' ? 0x052E16 : 0x2E0516, alpha: 0.92 });
          }
        }

        // --- Name labels (every 30 frames) ---
        if (tick % 30 === 0) {
          const lg = labelGraphicsRef.current;
          if (lg) {
            lg.clear();
            const states = avatarStatesRef.current;
            const bMap = behaviorMapRef.current;
            for (const avatar of states) {
              const bState = bMap[avatar.modelName];
              const drawX = bState ? bState.currentX : avatar.deskX;
              const drawY = bState ? bState.currentY : avatar.deskY + AVATAR_Y_OFFSET;
              drawNameLabel(lg, avatar.displayName, drawX, drawY + 24 * SCALE_FACTOR + 6, avatar.position !== null);
            }
            // Manager label with "BOSS" text
            const mPos = managerPosRef.current;
            drawNameLabel(lg, 'BOSS', mPos.x, mPos.y - 4 * SCALE_FACTOR + 24 * SCALE_FACTOR + 6, true);
          }
        }
      });
    };

    init();
    return () => {
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT, imageRendering: 'pixelated' }}
    />
  );
};

export default PixiOffice;
